import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Anchor,
    Body1,
    Body2,
    Flex,
    IconCached,
    IconEvent,
    IconEventBusy,
    IconNoteAlt,
    IconNumbers,
    IconStethoscope,
    IconUpdate,
    Stack,
    formatPhoneNumber,
} from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import type { DigitallyReceivedPrescriberOrderInfoFragment } from '../../../graphql/generated/graphql';
import { formatToLocalizedDate } from '../../../util';
import { getAFormByCode } from '../../../util/formatting/get-a-form-by-code';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

type PrescriberOrderDetailsProps = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

const DataItem = ({
    description,
    icon,
}: {
    description: ReactNode;
    icon: ReactNode;
}) => {
    return (
        <Flex gap="xs">
            {icon}
            {description}
        </Flex>
    );
};

const PrescriberOrderDetails = ({
    prescriberOrder,
}: PrescriberOrderDetailsProps) => {
    const { i18n, t } = useTranslation('lilly');
    const configOverride = useMedicationConfigOverride();

    const {
        daysSupply,
        numberOfRefills,
        productInstruction,
        prescribedMedication,
        authorizedMedication,
    } = prescriberOrder;

    const validInstructions = productInstruction?.filter(Boolean);
    const instructions = validInstructions?.join(' ');
    const prescribedMed = getFragment(prescribedMedication);
    const prescribedMedicationNdc = prescribedMed.id;

    const authorizedMed = getFragment(authorizedMedication);
    const authorizedMedicationNdc =
        authorizedMed.__typename === 'UnknownMedication'
            ? authorizedMed.originalNdc || authorizedMed.id
            : authorizedMed.id;
    const authorizedOverride = configOverride.ndcInfo[authorizedMedicationNdc];
    const authorizedMedicationForm =
        authorizedMed.__typename === 'AuthorizedMedication'
            ? authorizedMed.formCode
            : authorizedOverride.dosageForm;

    const prescribedMedicationForm =
        prescribedMed.__typename === 'Medication'
            ? prescribedMed.formCode
            : undefined;

    const showDynamicDisclaimer =
        prescribedMedicationForm &&
        prescribedMedicationNdc !== authorizedMedicationNdc &&
        prescribedMedicationForm !== authorizedMedicationForm;

    // Get the appropriate 'a/an (blank)' string based on the form code
    const prescribedForm = getAFormByCode(prescribedMedicationForm, i18n);
    const authorizedForm = getAFormByCode(authorizedMedicationForm, i18n);

    const writtenDate = formatToLocalizedDate(
        new Date(prescriberOrder.writtenDate),
        { timeZone: 'UTC' },
        i18n.language
    );
    const expirationDate = prescriberOrder.expirationDate
        ? formatToLocalizedDate(
              new Date(prescriberOrder.expirationDate),
              { timeZone: 'UTC' },
              i18n.language
          )
        : null;

    const prescriber = getFragment(prescriberOrder.prescriber);
    const prescriberName = prescriber.fullNameWithTitle;
    const prescriberPhone = prescriber.phoneNumber;

    return (
        <Stack gap="sm" pb="sm">
            <DataItem
                description={
                    <Stack gap={0}>
                        <Body1>
                            {t('order.prescriber', {
                                name: prescriberName,
                            })}
                        </Body1>
                        <ConditionalComponent condition={!!prescriberPhone}>
                            <Anchor textDecoration="underline">
                                {formatPhoneNumber(prescriberPhone ?? '')}
                            </Anchor>
                        </ConditionalComponent>
                    </Stack>
                }
                icon={<IconStethoscope color="secondary" />}
            />
            <DataItem
                description={<Body1>{instructions}</Body1>}
                icon={<IconNoteAlt color="secondary" />}
            />

            <ConditionalComponent condition={!!daysSupply}>
                <DataItem
                    description={
                        <Body1>
                            {t('order.daysSupply', {
                                count: Number(daysSupply),
                            })}
                        </Body1>
                    }
                    icon={<IconUpdate color="secondary" />}
                />
            </ConditionalComponent>
            <DataItem
                description={
                    <Body1>
                        {t('order.refillCount', {
                            count: numberOfRefills,
                        })}
                    </Body1>
                }
                icon={<IconCached color="secondary" />}
            />
            <DataItem
                description={
                    <Body1>
                        {t('order.writtenOn', {
                            date: writtenDate,
                        })}
                    </Body1>
                }
                icon={<IconEvent color="secondary" />}
            />
            <ConditionalComponent condition={expirationDate !== null}>
                <DataItem
                    description={
                        <Body1>
                            {t('order.expiresOn', {
                                date: expirationDate,
                            })}
                        </Body1>
                    }
                    icon={<IconEventBusy color="secondary" />}
                />
            </ConditionalComponent>
            <ConditionalComponent condition={!!authorizedMedicationNdc}>
                <DataItem
                    description={
                        <Body1>
                            {t('order.ndc', {
                                ndc: authorizedMedicationNdc,
                            })}
                        </Body1>
                    }
                    icon={<IconNumbers color="secondary" />}
                />
            </ConditionalComponent>
            <Stack gap="xxs">
                {showDynamicDisclaimer ? (
                    <Body2>
                        {t('order.dispenseVial', {
                            authorizedForm,
                            prescribedForm,
                        })}
                    </Body2>
                ) : null}
                <Body2>{t('order.officialPrescription')}</Body2>
            </Stack>
        </Stack>
    );
};

export default PrescriberOrderDetails;
